<template>
  <div class="progress-analytics">
    <!-- Summary Cards -->
    <div class="row mb-4">
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="ClockIcon" size="24" class="text-primary mb-2" />
            <h4 class="mb-1">{{ formatTime(analyticsData.total_study_time || 0) }}</h4>
            <small class="text-muted">Время обучения</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="BookOpenIcon" size="24" class="text-success mb-2" />
            <h4 class="mb-1">{{ analyticsData.subjects_studied || 0 }}</h4>
            <small class="text-muted">Предметов изучено</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="TrendingUpIcon" size="24" class="text-info mb-2" />
            <h4 class="mb-1">{{ getWeeklyAverage() }}</h4>
            <small class="text-muted">Уроков в неделю</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="CalendarIcon" size="24" class="text-warning mb-2" />
            <h4 class="mb-1">{{ getActiveWeeks() }}</h4>
            <small class="text-muted">Активных недель</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Weekly Progress Chart -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Прогресс по неделям</h5>
            <div class="chart-legend">
              <div class="legend-item">
                <div class="legend-color bg-primary mr-2"></div>
                <small>Завершенные уроки</small>
              </div>
            </div>
          </div>
          <div class="card-body">
            <canvas ref="weeklyChart" height="300"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Subject Performance -->
    <div class="row mb-4">
      <div class="col-lg-8 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Прогресс по предметам</h5>
          </div>
          <div class="card-body">
            <div v-if="subjectProgress.length > 0">
              <div
                v-for="subject in subjectProgress"
                :key="subject.subject_name"
                class="subject-progress mb-4"
              >
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">{{ subject.subject_name }}</h6>
                  <span class="text-muted">{{ subject.completed_components }} компонент{{ getComponentWordEnding(subject.completed_components) }}</span>
                </div>
                <div class="row mb-2">
                  <div class="col-md-6">
                    <small class="text-muted">Средний балл: </small>
                    <span :class="getScoreClass(subject.average_score)">{{ subject.average_score }}%</span>
                  </div>
                  <div class="col-md-6">
                    <small class="text-muted">Время изучения: </small>
                    <span>{{ formatTime(subject.time_spent) }}</span>
                  </div>
                </div>
                <b-progress
                  :value="subject.average_score"
                  :variant="getProgressVariant(subject.average_score)"
                  height="8px"
                  class="mb-2"
                />
                <small class="text-muted">Последняя активность: {{ formatDate(subject.last_activity) }}</small>
              </div>
            </div>
            <div v-else class="text-center py-3">
              <p class="text-muted">Данные по предметам отсутствуют</p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Время по предметам</h5>
          </div>
          <div class="card-body">
            <canvas ref="subjectTimeChart" width="200" height="200"></canvas>
            <div class="subject-time-list mt-3">
              <div
                v-for="(subject, index) in subjectProgress.slice(0, 5)"
                :key="subject.subject_name"
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <div class="d-flex align-items-center">
                  <div
                    class="legend-color mr-2"
                    :style="{ backgroundColor: getSubjectColor(index) }"
                  ></div>
                  <small>{{ subject.subject_name }}</small>
                </div>
                <small>{{ formatTime(subject.time_spent) }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Study Patterns -->
    <div class="row">
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Паттерны обучения</h5>
          </div>
          <div class="card-body">
            <div class="pattern-stats">
              <div class="stat-row d-flex justify-content-between align-items-center mb-3">
                <span>Самая продуктивная неделя:</span>
                <strong>{{ getMostProductiveWeek() }}</strong>
              </div>
              <div class="stat-row d-flex justify-content-between align-items-center mb-3">
                <span>Среднее время за урок:</span>
                <strong>{{ getAverageTimePerLesson() }}</strong>
              </div>
              <div class="stat-row d-flex justify-content-between align-items-center mb-3">
                <span>Самый изучаемый предмет:</span>
                <strong>{{ getMostStudiedSubject() }}</strong>
              </div>
              <div class="stat-row d-flex justify-content-between align-items-center">
                <span>Лучший предмет по баллам:</span>
                <strong>{{ getBestSubjectByScore() }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Тенденции</h5>
          </div>
          <div class="card-body">
            <div class="trends">
              <div class="trend-item mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <span>Тенденция активности:</span>
                  <div class="d-flex align-items-center">
                    <feather-icon
                      :icon="getActivityTrendIcon()"
                      :class="getActivityTrendClass()"
                      size="16"
                      class="mr-1"
                    />
                    <span :class="getActivityTrendClass()">{{ getActivityTrendText() }}</span>
                  </div>
                </div>
              </div>
              <div class="trend-item mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <span>Тенденция результатов:</span>
                  <div class="d-flex align-items-center">
                    <feather-icon
                      :icon="getScoreTrendIcon()"
                      :class="getScoreTrendClass()"
                      size="16"
                      class="mr-1"
                    />
                    <span :class="getScoreTrendClass()">{{ getScoreTrendText() }}</span>
                  </div>
                </div>
              </div>
              <div class="trend-item">
                <div class="d-flex justify-content-between align-items-center">
                  <span>Консистентность:</span>
                  <span :class="getConsistencyClass()">{{ getConsistencyText() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BProgress, BCard, BCardHeader, BCardBody } from 'bootstrap-vue'

export default {
  name: 'ProgressAnalytics',
  components: { BProgress, BCard, BCardHeader, BCardBody },
  props: {
    analyticsData: {
      type: Object,
      default: () => ({
        weekly_progress: [],
        total_study_time: 0,
        subjects_studied: 0
      })
    }
  },
  computed: {
    weeklyProgress() {
      return this.analyticsData.weekly_progress || []
    },
    subjectProgress() {
      return this.analyticsData.subject_progress || []
    }
  },
  methods: {
    getWeeklyAverage() {
      if (!this.weeklyProgress.length) return '0'
      const total = this.weeklyProgress.reduce((sum, week) => sum + week.completed, 0)
      return Math.round(total / this.weeklyProgress.length)
    },
    getActiveWeeks() {
      return this.weeklyProgress.filter(week => week.completed > 0).length
    },
    getMostProductiveWeek() {
      if (!this.weeklyProgress.length) return '-'
      const best = this.weeklyProgress.reduce((max, week) =>
        week.completed > max.completed ? week : max
      )
      return `${best.completed} уроков`
    },
    getAverageTimePerLesson() {
      const totalLessons = this.weeklyProgress.reduce((sum, week) => sum + week.completed, 0)
      if (!totalLessons) return '-'
      const avgMinutes = (this.analyticsData.total_study_time || 0) / totalLessons
      return this.formatTime(avgMinutes)
    },
    getMostStudiedSubject() {
      if (!this.subjectProgress.length) return '-'
      const most = this.subjectProgress.reduce((max, subject) =>
        subject.time_spent > max.time_spent ? subject : max
      )
      return most.subject_name
    },
    getBestSubjectByScore() {
      if (!this.subjectProgress.length) return '-'
      const best = this.subjectProgress.reduce((max, subject) =>
        subject.average_score > max.average_score ? subject : max
      )
      return best.subject_name
    },
    getActivityTrendIcon() {
      const trend = this.calculateActivityTrend()
      return trend > 0 ? 'TrendingUpIcon' : trend < 0 ? 'TrendingDownIcon' : 'MinusIcon'
    },
    getActivityTrendClass() {
      const trend = this.calculateActivityTrend()
      return trend > 0 ? 'text-success' : trend < 0 ? 'text-danger' : 'text-muted'
    },
    getActivityTrendText() {
      const trend = this.calculateActivityTrend()
      return trend > 0 ? 'Растет' : trend < 0 ? 'Снижается' : 'Стабильно'
    },
    getScoreTrendIcon() {
      // Simplified calculation
      return 'TrendingUpIcon'
    },
    getScoreTrendClass() {
      return 'text-success'
    },
    getScoreTrendText() {
      return 'Улучшается'
    },
    getConsistencyClass() {
      const consistency = this.calculateConsistency()
      return consistency > 70 ? 'text-success' : consistency > 40 ? 'text-warning' : 'text-danger'
    },
    getConsistencyText() {
      const consistency = this.calculateConsistency()
      if (consistency > 70) return 'Высокая'
      if (consistency > 40) return 'Средняя'
      return 'Низкая'
    },
    calculateActivityTrend() {
      if (this.weeklyProgress.length < 4) return 0
      const recent = this.weeklyProgress.slice(-4)
      const first = recent.slice(0, 2).reduce((sum, w) => sum + w.completed, 0) / 2
      const last = recent.slice(-2).reduce((sum, w) => sum + w.completed, 0) / 2
      return last - first
    },
    calculateConsistency() {
      if (!this.weeklyProgress.length) return 0
      const activeWeeks = this.weeklyProgress.filter(w => w.completed > 0).length
      return Math.round((activeWeeks / this.weeklyProgress.length) * 100)
    },
    getComponentWordEnding(count) {
      if (count === 1) return ''
      if (count >= 2 && count <= 4) return 'а'
      return 'ов'
    },
    getScoreClass(score) {
      if (score >= 90) return 'text-success font-weight-bold'
      if (score >= 80) return 'text-primary font-weight-bold'
      if (score >= 70) return 'text-info'
      return 'text-warning'
    },
    getProgressVariant(score) {
      if (score >= 90) return 'success'
      if (score >= 70) return 'primary'
      if (score >= 50) return 'warning'
      return 'danger'
    },
    getSubjectColor(index) {
      const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8']
      return colors[index % colors.length]
    },
    formatTime(minutes) {
      if (!minutes) return '0м'
      if (minutes < 60) return `${Math.round(minutes)}м`
      const hours = Math.floor(minutes / 60)
      const mins = Math.round(minutes % 60)
      return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit'
      })
    },
    createWeeklyChart() {
      const canvas = this.$refs.weeklyChart
      if (!canvas || !this.weeklyProgress.length) return

      const ctx = canvas.getContext('2d')
      const data = this.weeklyProgress

      const padding = 40
      const chartWidth = canvas.width - 2 * padding
      const chartHeight = canvas.height - 2 * padding
      const maxValue = Math.max(...data.map(d => d.completed), 10)

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw grid lines
      ctx.strokeStyle = '#e0e0e0'
      ctx.lineWidth = 1

      for (let i = 0; i <= 5; i++) {
        const y = padding + (i * chartHeight) / 5
        ctx.beginPath()
        ctx.moveTo(padding, y)
        ctx.lineTo(padding + chartWidth, y)
        ctx.stroke()
      }

      // Draw bars
      const barWidth = chartWidth / data.length * 0.8
      const barSpacing = chartWidth / data.length * 0.2

      data.forEach((week, index) => {
        const barHeight = (week.completed / maxValue) * chartHeight
        const x = padding + index * (barWidth + barSpacing)
        const y = padding + chartHeight - barHeight

        ctx.fillStyle = '#007bff'
        ctx.fillRect(x, y, barWidth, barHeight)

        // Draw week labels
        ctx.fillStyle = '#6c757d'
        ctx.font = '10px Arial'
        ctx.textAlign = 'center'
        ctx.fillText(
          this.formatDate(week.week),
          x + barWidth / 2,
          padding + chartHeight + 20
        )
      })
    },
    createSubjectTimeChart() {
      const canvas = this.$refs.subjectTimeChart
      if (!canvas || !this.subjectProgress.length) return

      const ctx = canvas.getContext('2d')
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const radius = Math.min(centerX, centerY) - 10

      const totalTime = this.subjectProgress.reduce((sum, s) => sum + s.time_spent, 0)
      if (totalTime === 0) return

      let startAngle = -Math.PI / 2

      this.subjectProgress.slice(0, 5).forEach((subject, index) => {
        const sliceAngle = (subject.time_spent / totalTime) * 2 * Math.PI

        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle)
        ctx.lineTo(centerX, centerY)
        ctx.fillStyle = this.getSubjectColor(index)
        ctx.fill()

        startAngle += sliceAngle
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.createWeeklyChart()
      this.createSubjectTimeChart()
    })
  },
  watch: {
    analyticsData: {
      handler() {
        this.$nextTick(() => {
          this.createWeeklyChart()
          this.createSubjectTimeChart()
        })
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.progress-analytics {
  .stats-card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .subject-progress {
    padding: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    background: #f8f9fa;
  }

  .chart-legend {
    .legend-item {
      display: flex;
      align-items: center;
    }
  }

  .legend-color {
    width: 12px;
    height: 12px;
    border-radius: 2px;
  }

  .pattern-stats,
  .trends {
    .stat-row,
    .trend-item {
      padding: 0.5rem 0;
      border-bottom: 1px solid #e9ecef;

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .subject-time-list {
    font-size: 0.9rem;
  }
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.dark-layout {
  .progress-analytics {
    .stats-card,
    .card {
      background-color: #283046;
      color: #d0d2d6;
    }

    .subject-progress {
      background-color: #404656;
      border-color: #555;
    }

    .pattern-stats,
    .trends {
      .stat-row,
      .trend-item {
        border-bottom-color: #555;
      }
    }

    .text-muted {
      color: #b4b7bd !important;
    }
  }
}
</style>